import React, { useEffect, useId, useRef } from "react";
import Icon from "../icons/Icon";
import "../../styles/components/app-modal.css";

/**
 * Shared modal shell (PRD_ModalBehavior C1–C9).
 *
 * Owns the fixed layer, the backdrop, the dialog role, the pinned × button,
 * Esc, the Tab focus trap, focus return and the body scroll lock. The call
 * site owns everything inside: its own header (pass its heading id as
 * `labelledBy`), body and footer, including the Cancel button that it disables
 * with the same `busy` it passes here (C4).
 *
 * Backdrop clicks are ignored on purpose (C1). Esc and × call `onClose` only
 * when the modal is topmost (C7) and not `busy` (C4).
 *
 * @param {boolean} isOpen - Renders nothing when false
 * @param {function} onClose - Called by × and Esc
 * @param {string} labelledBy - id of the call site's heading (aria-labelledby)
 * @param {boolean} busy - Locks Esc and × while an action is in flight
 * @param {string} className - Extra class on the dialog (the call site's card look)
 * @param {object} style - Inline style on the dialog (the call site's card look)
 * @param {React.ReactNode} children - Header, body and footer
 */

// Ids of the open modals, oldest first (C7). Each modal registers itself
// while open and removes its OWN id on close — never a blind pop — so
// StrictMode's doubled effects and a close-A/open-B commit both stay correct.
const openStack = [];
// body overflow before the first modal opened; restored when the last closes.
let priorBodyOverflow = null;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
  'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function Modal({ isOpen, onClose, labelledBy, busy = false, className, style, children }) {
  const id = useId();
  const dialogRef = useRef(null);
  // Latest values for the key listener, so it is bound once per open.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  // C9: who had focus before this modal's first open. Read during render, not
  // in the effect — React applies a child's autoFocus at commit, so by the time
  // the effect runs activeElement is already a field inside the dialog, and that
  // field is gone by the time the modal closes. Reset on close so the next open
  // captures afresh; the effect copies it into a closure before that happens.
  const openerRef = useRef(null);
  if (isOpen && openerRef.current === null) openerRef.current = document.activeElement;
  if (!isOpen) openerRef.current = null;

  useEffect(() => {
    if (!isOpen) return undefined;

    // Use the render-time opener only if it is still on the page and is not
    // `body` (`body` means nothing had focus at render — a browser blurs a
    // control the moment it is disabled, so a modal opened from a button that
    // disables itself mid-submit captures nothing). Otherwise re-read focus
    // here: React runs a closing modal's cleanup before this setup, so when two
    // modals swap in one update (Add Kiosk → Pairing Code) that cleanup has
    // just put focus back on the element that started the sequence. With no
    // modal closing there is nothing to re-read and this lands on `body`.
    const captured = openerRef.current;
    const usable = captured && captured !== document.body && captured.isConnected;
    const opener = usable ? captured : document.activeElement;
    // Keep the resolved element: StrictMode replays this effect on the dev
    // server, and re-reading focus there would adopt this dialog as its own
    // opener — leaving nothing to return focus to on close.
    openerRef.current = opener;
    openStack.push(id);
    if (openStack.length === 1) {
      priorBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }

    // C9: leave focus alone if a child already took it (autoFocus); else the
    // dialog itself, so the next Tab lands on its first control.
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus();

    const onKeyDown = (e) => {
      if (openStack[openStack.length - 1] !== id) return; // not topmost (C7)
      if (e.key === "Escape") {
        if (!busyRef.current) onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const outside = active === dialogRef.current || !dialogRef.current.contains(active);
      if (!first) {
        e.preventDefault();
        dialogRef.current.focus();
      } else if (e.shiftKey && (active === first || outside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || outside)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      const at = openStack.indexOf(id);
      if (at !== -1) openStack.splice(at, 1);
      if (openStack.length === 0) {
        document.body.style.overflow = priorBodyOverflow;
        priorBodyOverflow = null;
      }
      // C9: back to the opener, but only if it is still on the page — and not
      // while THIS dialog is still on the page either: that is StrictMode's
      // simulated unmount on the dev server (a real close removes the node
      // before this cleanup runs), and handing focus back mid-mount would pull
      // it off an autoFocused child.
      if (dialog && dialog.isConnected) return;
      if (!opener || opener === document.body) return;
      if (opener.isConnected && typeof opener.focus === "function") opener.focus();
    };
  }, [isOpen, id]);

  if (!isOpen) return null;

  return (
    <div className="app-modal">
      <div className="app-modal__backdrop" data-testid="modal-backdrop" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className={className ? `app-modal__dialog ${className}` : "app-modal__dialog"}
        style={style}
      >
        <button
          type="button"
          className="app-modal__close"
          aria-label="Close"
          disabled={busy}
          onClick={onClose}
        >
          <Icon name="x" size={18} />
        </button>
        {children}
      </div>
    </div>
  );
}
